import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Download,
  GraduationCap,
  Plus,
  Search,
  Trash2,
  Upload,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  ContextHero,
  KpiCard,
  MoneyAmount,
  EmptyStateModern,
  PageContainer,
  SectionTitle,
} from '@/components/design-system';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useAllActivites } from '@/hooks/queries/useActivites';
import {
  usePaiementsFormateurs,
  useDeletePaiementFormateur,
  type PaiementFormateurRow,
  type PaiementFormateurStatut,
} from '@/hooks/queries/usePaiementsFormateurs';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { ImportConsolideFormateursDialog } from '@/components/imports/ImportConsolideFormateursDialog';
import { MarquerPayeFormateurDialog } from '@/components/forms/MarquerPayeFormateurDialog';
import { MarquerPayesEnLotDialog } from '@/components/forms/MarquerPayesEnLotDialog';
import { CreatePaiementFormateurDialog } from '@/components/forms/CreatePaiementFormateurDialog';
import { PaiementFormateurDrawer } from '@/components/finances/PaiementFormateurDrawer';
import { StatutPaiementBadge, isPaiementEnRetard } from '@/components/finances/StatutPaiementBadge';
import { cn } from '@/lib/utils';
import { usePermission } from '@/hooks/usePermission';
import { AccessDenied } from '@/components/AccessDenied';

const STATUT_LABEL: Record<PaiementFormateurStatut, string> = {
  paye: 'Payé',
  a_payer: 'Non payé',
  non_retribue: 'Non rétribué',
};

const STATUT_FILTERS: { value: PaiementFormateurStatut | ''; label: string }[] = [
  { value: '', label: 'Tous' },
  { value: 'paye', label: 'Payés' },
  { value: 'a_payer', label: 'Non payés' },
  { value: 'non_retribue', label: 'Non rétribués' },
];

export function ConsolideFormateursPage(): JSX.Element {
  const canView = usePermission('consolide_formateurs.view');
  const canEdit = usePermission('consolide_formateurs.edit');
  const { data: entreprises = [] } = useEntreprises();
  const { data: activites = [] } = useAllActivites();

  // Default = EURL Kost Group (Formation B2B). Si pas trouvé, 1ère entreprise.
  const eurl =
    entreprises.find((e) => /kost/i.test(e.nom ?? '') && /eurl/i.test(e.nom ?? '')) ??
    entreprises[0] ??
    null;
  const [entrepriseId, setEntrepriseId] = useState<string>('');
  const effectiveEntrepriseId = entrepriseId || eurl?.id || '';
  const activiteB2B = useMemo(
    () =>
      activites.find(
        (a) => a.entreprise_id === effectiveEntrepriseId && /b2b|formation b2b/i.test(a.nom ?? ''),
      ),
    [activites, effectiveEntrepriseId],
  );

  // Sprint 6.9.1.2 : default '' (Toutes années) — l'init à 2026 cachait les données antérieures.
  const [annee, setAnnee] = useState<number | ''>('');
  const [statut, setStatut] = useState<PaiementFormateurStatut | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 300);
  const [importOpen, setImportOpen] = useState(false);
  const [marquerOpen, setMarquerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editPaiement, setEditPaiement] = useState<PaiementFormateurRow | null>(null);
  const [bulkPayOpen, setBulkPayOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<PaiementFormateurRow | null>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [activePaiement, setActivePaiement] = useState<PaiementFormateurRow | null>(null);
  const [drawerPaiement, setDrawerPaiement] = useState<PaiementFormateurRow | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data: paiements = [], isLoading } = usePaiementsFormateurs({
    entrepriseId: effectiveEntrepriseId || null,
    annee: annee === '' ? null : annee,
    statut: statut || null,
    search,
  });
  const deletePaiement = useDeletePaiementFormateur();

  // Sprint 6.9.1.2 : on inclut TOUJOURS [2020..currentYear+1] pour ne pas dépendre du filtre.
  const yearsAvailable = useMemo(() => {
    const set = new Set<number>();
    const current = new Date().getFullYear();
    for (let y = 2020; y <= current + 1; y++) set.add(y);
    for (const p of paiements) set.add(p.annee);
    return Array.from(set).sort((a, b) => b - a);
  }, [paiements]);

  const selectedPaiements = useMemo(
    () => paiements.filter((p) => selectedIds.has(p.id)),
    [paiements, selectedIds],
  );

  const kpis = useMemo(() => {
    const totalPaye = paiements
      .filter((p) => p.statut === 'paye')
      .reduce((a, p) => a + p.montant, 0);
    const totalAPayer = paiements
      .filter((p) => p.statut === 'a_payer')
      .reduce((a, p) => a + p.montant, 0);
    const totalNonRetribue = paiements
      .filter((p) => p.statut === 'non_retribue')
      .reduce((a, p) => a + p.montant, 0);
    const byFormateur = new Map<string, number>();
    for (const p of paiements) {
      const k = p.formateur_nom;
      byFormateur.set(k, (byFormateur.get(k) ?? 0) + p.montant);
    }
    let topFormateur: { nom: string; total: number } | null = null;
    for (const [nom, total] of byFormateur) {
      if (!topFormateur || total > topFormateur.total) topFormateur = { nom, total };
    }
    return { totalPaye, totalAPayer, totalNonRetribue, topFormateur };
  }, [paiements]);

  const toggleSelect = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = (): void => {
    if (selectedIds.size === paiements.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(paiements.map((p) => p.id)));
  };

  const exportSelectionCSV = (): void => {
    const rows = paiements.filter((p) => selectedIds.has(p.id));
    if (rows.length === 0) {
      toast.warning('Aucune ligne sélectionnée');
      return;
    }
    const headers = [
      'Année',
      'Période',
      'Formateur',
      'Coordonnées',
      'Formation',
      'Date déroulement',
      'Montant',
      'Statut',
      'Date règlement',
      'Mode',
      'Référence',
      'Remarques',
    ];
    const csvLines = rows.map((p) =>
      [
        p.annee,
        p.periode ?? '',
        p.formateur_nom,
        p.formateur_coordonnees ?? '',
        p.formation_titre ?? '',
        p.date_deroulement ?? '',
        p.montant,
        STATUT_LABEL[p.statut],
        p.date_reglement ?? '',
        p.mode_paiement ?? '',
        p.reference_operation ?? '',
        (p.remarques ?? '').replace(/"/g, '""'),
      ]
        .map((v) => {
          const s = String(v ?? '');
          return /[",\n;]/.test(s) ? `"${s}"` : s;
        })
        .join(','),
    );
    const csv = [headers.join(','), ...csvLines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `paiements_formateurs_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${rows.length} ligne(s) exportée(s)`);
  };

  if (!canView) return <AccessDenied feature="consolide_formateurs.view" />;

  return (
    <PageContainer>
      <ContextHero
        variant="formation_b2b"
        icon={<GraduationCap className="h-5 w-5" />}
        title="Consolidé Formateurs"
        subtitle="Suivi des paiements formateurs B2B — import Excel + cross-références automatiques vers la Caisse"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={effectiveEntrepriseId}
              onChange={(e) => setEntrepriseId(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
              aria-label="Entreprise"
            >
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
            {canEdit && (
              <>
                <button
                  type="button"
                  onClick={() => setImportOpen(true)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <Upload className="h-3.5 w-3.5" /> Importer Excel
                </button>
                <button
                  type="button"
                  onClick={() => setCreateOpen(true)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md bg-violet-600 px-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-violet-700"
                >
                  <Plus className="h-3.5 w-3.5" /> Nouveau paiement
                </button>
              </>
            )}
          </div>
        }
      />

      {/* KPIs */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Total payé"
          value={<MoneyAmount amount={kpis.totalPaye} variant="kpi" />}
          variant="formation_b2b"
          icon={<CheckCircle2 className="h-4 w-4" />}
          loading={isLoading}
        />
        <KpiCard
          label="À payer"
          value={<MoneyAmount amount={kpis.totalAPayer} variant="kpi" />}
          variant="finances"
          loading={isLoading}
        />
        <KpiCard
          label="Non rétribué"
          value={<MoneyAmount amount={kpis.totalNonRetribue} variant="kpi" />}
          variant="dashboard"
          loading={isLoading}
        />
        <KpiCard
          label="Top formateur (CA)"
          value={
            <span className="text-base font-bold text-slate-900">
              {kpis.topFormateur?.nom ?? '—'}
            </span>
          }
          hint={
            kpis.topFormateur ? `${kpis.topFormateur.total.toLocaleString('fr-DZ')} DA` : undefined
          }
          variant="formation_b2b"
          loading={isLoading}
        />
      </div>

      {/* Filtres */}
      <div className="mt-5 flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 bg-white p-3">
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Année</label>
          <select
            value={annee === '' ? '' : annee}
            onChange={(e) => setAnnee(e.target.value === '' ? '' : Number(e.target.value))}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
          >
            <option value="">— Toutes —</option>
            {yearsAvailable.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Statut</label>
          <div className="flex gap-1">
            {STATUT_FILTERS.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => setStatut(s.value)}
                className={cn(
                  'h-9 rounded-md border px-3 text-xs font-semibold transition-colors',
                  statut === s.value
                    ? 'border-violet-500 bg-violet-50 text-violet-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                )}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="ml-auto space-y-1">
          <label className="text-xs text-slate-500">Recherche</label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Formateur, formation, référence…"
              className="h-9 w-64 pl-7"
            />
          </div>
        </div>
        {(annee !== '' || statut || searchInput) && (
          <button
            type="button"
            onClick={() => {
              setAnnee('');
              setStatut('');
              setSearchInput('');
            }}
            className="inline-flex h-9 items-center gap-1 rounded-md px-3 text-xs text-slate-500 hover:bg-slate-100"
          >
            <XCircle className="h-3 w-3" /> Effacer
          </button>
        )}
      </div>

      {/* Sprint 6.9.1.2 — Sticky bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="sticky top-2 z-20 mt-5 flex flex-wrap items-center gap-2 rounded-xl border border-violet-300 bg-violet-50/95 px-4 py-2.5 shadow-md backdrop-blur">
          <span className="text-sm font-bold text-violet-900">
            {selectedIds.size} paiement{selectedIds.size > 1 ? 's' : ''} sélectionné
            {selectedIds.size > 1 ? 's' : ''}
          </span>
          <span className="text-xs text-violet-700">
            ({selectedPaiements.filter((p) => p.statut !== 'paye').length} payable
            {selectedPaiements.filter((p) => p.statut !== 'paye').length > 1 ? 's' : ''})
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkPayOpen(true)}
              disabled={selectedPaiements.filter((p) => p.statut !== 'paye').length === 0}
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-md bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-700',
                selectedPaiements.filter((p) => p.statut !== 'paye').length === 0 &&
                  'cursor-not-allowed opacity-50',
              )}
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Marquer payés en lot
            </button>
            <button
              type="button"
              onClick={exportSelectionCSV}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Download className="h-3.5 w-3.5" /> Exporter CSV
            </button>
            <button
              type="button"
              onClick={() => setConfirmBulkDelete(true)}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-rose-600 px-3 text-xs font-semibold text-white hover:bg-rose-700"
            >
              <Trash2 className="h-3.5 w-3.5" /> Supprimer
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs text-slate-500 hover:bg-slate-100"
            >
              <XCircle className="h-3 w-3" /> Désélectionner
            </button>
          </div>
        </div>
      )}

      {/* Tableau Excel-like */}
      <div className="mt-5">
        <SectionTitle
          trailing={
            <span className="text-xs text-slate-500">
              {paiements.length} paiement{paiements.length > 1 ? 's' : ''}
            </span>
          }
        >
          Paiements
        </SectionTitle>
        <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white">
          {isLoading ? (
            <div className="space-y-1 p-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-9 animate-pulse rounded-md bg-slate-100" />
              ))}
            </div>
          ) : paiements.length === 0 ? (
            <div className="p-6">
              <EmptyStateModern
                variant="formation_b2b"
                icon={<GraduationCap className="h-6 w-6" />}
                title="Aucun paiement formateur"
                description="Importez un Excel consolidé ou créez un paiement manuellement pour démarrer."
                ctaLabel="Importer Excel"
                onCtaClick={() => setImportOpen(true)}
              />
            </div>
          ) : (
            <div className="scrollbar-thin overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/80 text-[10px] font-bold uppercase tracking-widest text-slate-600 backdrop-blur">
                  <tr>
                    <th className="px-2 py-2 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === paiements.length && paiements.length > 0}
                        onChange={toggleAll}
                        aria-label="Tout sélectionner"
                      />
                    </th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Année</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Formateur</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Formation</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Date</th>
                    <th className="whitespace-nowrap px-2 py-2 text-right">Montant</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Statut</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Date règlement</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Mode</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Référence</th>
                    <th className="whitespace-nowrap px-2 py-2 text-left">Remarques</th>
                    <th className="px-2 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paiements.map((p) => {
                    const enRetard = isPaiementEnRetard(p.statut, p.date_deroulement);
                    const rowTone = enRetard
                      ? 'bg-rose-50/40 border-l-2 border-l-rose-300'
                      : p.statut === 'paye'
                        ? 'bg-emerald-50/30'
                        : p.statut === 'non_retribue'
                          ? 'bg-slate-50/50'
                          : 'bg-white';
                    return (
                      <tr
                        key={p.id}
                        className={cn(
                          'border-b border-slate-100 transition-colors hover:bg-slate-50',
                          rowTone,
                        )}
                      >
                        <td className="px-2 py-1.5">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(p.id)}
                            onChange={() => toggleSelect(p.id)}
                            aria-label={`Sélectionner ${p.formateur_nom}`}
                          />
                        </td>
                        <td className="px-2 py-1.5 tabular-nums">{p.annee}</td>
                        <td
                          className="cursor-pointer px-2 py-1.5 font-semibold text-slate-800 hover:underline"
                          onClick={() => setDrawerPaiement(p)}
                        >
                          {p.formateur_nom}
                        </td>
                        <td className="px-2 py-1.5 text-slate-600">{p.formation_titre ?? '—'}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-slate-600">
                          {p.date_deroulement ?? '—'}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-right">
                          <MoneyAmount amount={p.montant} />
                        </td>
                        <td className="px-2 py-1.5">
                          <StatutPaiementBadge statut={p.statut} enRetard={enRetard} size="sm" />
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-slate-600">
                          {p.date_reglement ?? '—'}
                        </td>
                        <td className="px-2 py-1.5 text-slate-600">{p.mode_paiement ?? '—'}</td>
                        <td className="whitespace-nowrap px-2 py-1.5 font-mono text-[10px] text-slate-600">
                          {p.reference_operation ?? '—'}
                        </td>
                        <td className="px-2 py-1.5 text-slate-500">
                          <span className="line-clamp-1 max-w-[200px]">{p.remarques ?? '—'}</span>
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          {p.statut === 'a_payer' ? (
                            <button
                              type="button"
                              onClick={() => {
                                setActivePaiement(p);
                                setMarquerOpen(true);
                              }}
                              className="inline-flex h-7 items-center gap-1 rounded-md bg-emerald-600 px-2 text-xs font-semibold text-white hover:bg-emerald-700"
                            >
                              <CheckCircle2 className="h-3 w-3" /> Payer
                            </button>
                          ) : p.operation_id ? (
                            <Badge variant="outline" className="text-[10px]">
                              Op liée
                            </Badge>
                          ) : (
                            <span className="text-[10px] text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals & drawer */}
      <ImportConsolideFormateursDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        entrepriseId={effectiveEntrepriseId}
        activiteId={activiteB2B?.id ?? null}
      />
      <MarquerPayeFormateurDialog
        open={marquerOpen}
        onOpenChange={(o) => {
          setMarquerOpen(o);
          if (!o) setActivePaiement(null);
        }}
        paiement={activePaiement}
      />
      <PaiementFormateurDrawer
        paiement={drawerPaiement}
        onClose={() => setDrawerPaiement(null)}
        onMarquerPaye={(p) => {
          setActivePaiement(p);
          setMarquerOpen(true);
          setDrawerPaiement(null);
        }}
        onEdit={(p) => {
          setEditPaiement(p);
          setDrawerPaiement(null);
        }}
        onDelete={(p) => {
          setConfirmDelete(p);
          setDrawerPaiement(null);
        }}
      />

      {/* Sprint 6.9.1.2 — CRUD modals */}
      <CreatePaiementFormateurDialog
        open={createOpen || editPaiement !== null}
        onOpenChange={(o) => {
          if (!o) {
            setCreateOpen(false);
            setEditPaiement(null);
          }
        }}
        entrepriseId={effectiveEntrepriseId}
        activiteId={activiteB2B?.id ?? null}
        paiement={editPaiement}
      />

      <MarquerPayesEnLotDialog
        open={bulkPayOpen}
        onOpenChange={setBulkPayOpen}
        paiements={selectedPaiements}
        entrepriseId={effectiveEntrepriseId}
        onDone={() => setSelectedIds(new Set())}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={
          confirmDelete
            ? `Supprimer le paiement de ${confirmDelete.formateur_nom} ?`
            : 'Supprimer ?'
        }
        description={
          confirmDelete?.operation_id
            ? "Cette action est irréversible. L'opération Caisse liée sera également supprimée."
            : 'Cette action est irréversible.'
        }
        loading={deletePaiement.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          try {
            await deletePaiement.mutateAsync({
              id: confirmDelete.id,
              operation_id: confirmDelete.operation_id,
            });
            toast.success('Paiement supprimé');
            setConfirmDelete(null);
          } catch (e) {
            toast.error(`Échec : ${(e as Error).message}`);
          }
        }}
      />

      <ConfirmDialog
        open={confirmBulkDelete}
        onOpenChange={setConfirmBulkDelete}
        destructive
        title={`Supprimer ${selectedIds.size} paiement(s) ?`}
        description="Action irréversible. Les opérations Caisse liées seront aussi supprimées si présentes."
        loading={deletePaiement.isPending}
        onConfirm={async () => {
          let ok = 0;
          const errs: string[] = [];
          for (const p of selectedPaiements) {
            try {
              await deletePaiement.mutateAsync({ id: p.id, operation_id: p.operation_id });
              ok++;
            } catch (e) {
              errs.push((e as Error).message);
            }
          }
          if (errs.length === 0) toast.success(`${ok} paiement(s) supprimé(s)`);
          else toast.warning(`${ok} OK · ${errs.length} échec(s)`);
          setConfirmBulkDelete(false);
          setSelectedIds(new Set());
        }}
      />
    </PageContainer>
  );
}
